<template>
  <div>
    <h2>Welcome to MEMBERS ONLY</h2>
    <p>
      Fill in your details to create an account and enjoy early sale access,
      exclusive benefits plus much more.
    </p>

    <SingleSignOn theme="primary-light" />

    <div class="divider">or</div>

    <div v-if="hasErrors" class="error-messages">
      <ul>
        <li v-for="error in errors" :key="error.code">
          {{ error.message }}
        </li>
      </ul>
    </div>

    <form class="form" @submit.prevent="handleFormSubmit">
      <TextField
        theme="white"
        :value="email"
        :validation="v$.email"
        autocomplete="email"
        type="email"
        placeholder="E-mail address"
        @input="(value) => (email = value)"
      />

      <TextField
        theme="white"
        :value="password"
        :validation="v$.password"
        autocomplete="new-password"
        type="password"
        placeholder="Password"
        @input="(value) => (password = value)"
      />

      <div class="field">
        <Button
          type="submit"
          width="full"
          :loading="loading"
          theme="primary-light"
        >
          Sign up with email
        </Button>
      </div>
    </form>

    <p class="footnote">
      By creating an account, you agree to receive promotional emails and
      exclusive offers from Rat & Boa.
    </p>

    <Button width="full" theme="outline" @click="$emit('login')">
      Already a member?
    </Button>
  </div>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { required, email, helpers } from "@vuelidate/validators";
import SingleSignOn from "@/components/SingleSignOn.vue";
import TextField from "@/components/TextField.vue";
import Button from "@/components/ButtonV3.vue";
import isEmpty from "lodash-es/isEmpty";
import { mapActions, mapState } from "vuex";

export default {
  name: "LandingRegisterForm",
  components: {
    SingleSignOn,
    TextField,
    Button,
  },
  props: {
    endDate: {
      type: String,
      required: true,
    },
  },
  emits: ["success", "login"],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      email: "",
      password: "",
      loading: false,
      errors: [],
    };
  },
  validations() {
    return {
      email: {
        required: helpers.withMessage("Email address is required", required),
        email,
      },
      password: {
        required: helpers.withMessage("Password is required", required),
      },
    };
  },
  computed: {
    ...mapState("auth", ["multipassAccount"]),
    hasErrors() {
      return this.errors.length > 0;
    },
  },
  methods: {
    ...mapActions("auth", ["attemptRegister"]),
    async handleFormSubmit() {
      this.v$.$touch();

      if (this.v$.$invalid) {
        return;
      }

      const registerInput = {
        email: this.email,
        password: this.password,
        returnUrl: "/",
      };

      this.loading = true;
      const data = await this.attemptRegister(registerInput);

      if (data.customerUserErrors.length >= 1) {
        this.errors = data.customerUserErrors;
        this.loading = false;
      } else {
        if (!isEmpty(data.customerAccessToken)) {
          window.location.href = this.multipassAccount.url;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.button-group {
  display: flex;
  gap: 20px;
  flex-direction: column;
}

:deep(.button--outline) {
  border-color: $color-white;
  color: $color-white;
}

h2 {
  @include editorial-headline(24px);
  text-transform: uppercase;
  margin: 0 auto 16px;
  max-width: 300px;

  @include respond-above(md) {
    max-width: auto;
  }
}

p {
  @include editorial-body(14px);
  margin-bottom: 28px;
  margin: 0 auto 28px;
  max-width: 300px;
  @include respond-above(md) {
    max-width: auto;
  }
}

.divider {
  position: relative;
  text-align: center;
  text-transform: uppercase;
  margin-bottom: 28px;
  @include editorial-body(14px);

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    border-bottom: solid 1px #d4d4d4;
    width: 40%;
    left: 0;
  }

  &::after {
    left: auto;
    right: 0;
  }
}

.sso-buttons {
  border-bottom: none;
  margin: 0 0 28px;
  padding: 0;
}

.footnote {
  margin-top: 28px;
  @include utility-label(13px);
  text-align: center;
}

.error-messages {
  margin-top: 10px;
  margin-bottom: 10px;
  background-color: $color-red;
  padding: 16px;
  color: $color-white;
  border-radius: 2px;
  font-size: 10px;
}
</style>
